import { SxProps, Theme } from '@mui/material';

export type DpFanQuickCardVariant = 'large' | 'medium' | 'horizontal';

type ResponsiveValue<T> = T | Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', T>>;

export type VariantTokens = {
	direction: 'column' | 'row';
	cardWidth: ResponsiveValue<string>;
	cardPadding: ResponsiveValue<string>;
	cardGap: ResponsiveValue<string>;
	cardBorder: string;
	cardRadius: string;
	cardBackground: string;

	imageWidth: ResponsiveValue<string>;
	imageHeight: ResponsiveValue<string>;
	imageRadius: string;

	infoPadding: ResponsiveValue<string>;
	infoGap: string;
	infoAlign: 'flex-start' | 'center';
	textAlign: 'left' | 'center';

	titleFontSize: ResponsiveValue<string>;
	titleFontWeight: number;
	titleLineHeight: string;

	subtitleFontSize?: ResponsiveValue<string>;
	subtitleLineHeight?: string;

	ctaMode: 'button' | 'link';
	ctaAlign: 'stretch' | 'flex-start';
	ctaMarginTop: string;
};

export const VARIANTS: Record<DpFanQuickCardVariant, VariantTokens> = {
	large: {
		direction: 'column',
		cardWidth: { xs: '100%', sm: '360px' },
		cardPadding: '0px',
		cardGap: '20px',
		cardBorder: 'none',
		cardRadius: '0px',
		cardBackground: 'transparent',

		imageWidth: '100%',
		imageHeight: { xs: '260px', sm: '300px' },
		imageRadius: '0px',

		infoPadding: '0px',
		infoGap: '8px',
		infoAlign: 'center',
		textAlign: 'center',

		titleFontSize: { xs: '18px', sm: '20px' },
		titleFontWeight: 800,
		titleLineHeight: '24px',

		subtitleFontSize: '15px',
		subtitleLineHeight: '20px',

		ctaMode: 'button',
		ctaAlign: 'stretch',
		ctaMarginTop: '12px',
	},

	medium: {
		direction: 'column',
		cardWidth: { xs: '100%', sm: '300px' },
		cardPadding: '0px',
		cardGap: '12px',
		cardBorder: 'none',
		cardRadius: '0px',
		cardBackground: 'transparent',

		imageWidth: '100%',
		imageHeight: '180px',
		imageRadius: '6px',

		infoPadding: '0px',
		infoGap: '4px',
		infoAlign: 'flex-start',
		textAlign: 'left',

		titleFontSize: '18px',
		titleFontWeight: 800,
		titleLineHeight: '22px',

		subtitleFontSize: '15px',
		subtitleLineHeight: '20px',

		ctaMode: 'link',
		ctaAlign: 'flex-start',
		ctaMarginTop: '4px',
	},

	horizontal: {
		direction: 'row',
		cardWidth: '100%',
		cardPadding: '0px',
		cardGap: '16px',
		cardBorder: '1px solid #DADCE0',
		cardRadius: '6px',
		cardBackground: '#FFFFFF',

		imageWidth: { xs: '100px', sm: '140px' },
		imageHeight: 'auto',
		imageRadius: '0px',

		infoPadding: '12px 16px 12px 0px',
		infoGap: '6px',
		infoAlign: 'flex-start',
		textAlign: 'left',

		titleFontSize: { xs: '15px', sm: '16px' },
		titleFontWeight: 800,
		titleLineHeight: '20px',

		ctaMode: 'link',
		ctaAlign: 'flex-start',
		ctaMarginTop: '2px',
	},
};

export const cardSX = (t: VariantTokens): SxProps<Theme> => ({
	display: 'flex',
	flexDirection: t.direction,
	alignItems: 'stretch',
	width: t.cardWidth,
	padding: t.cardPadding,
	gap: t.cardGap,
	border: t.cardBorder,
	borderRadius: t.cardRadius,
	backgroundColor: t.cardBackground,
	boxSizing: 'border-box',
	overflow: 'hidden',
});

export const imageFrameSX = (t: VariantTokens): SxProps<Theme> => ({
	flex: '0 0 auto',
	width: t.imageWidth,
	height: t.imageHeight,
	borderRadius: t.imageRadius,
	overflow: 'hidden',
	backgroundColor: '#F1F1F1',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const imageSX: SxProps<Theme> = {
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	display: 'block',
};

export const infoSX = (t: VariantTokens): SxProps<Theme> => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	alignItems: t.infoAlign,
	justifyContent: 'center',
	padding: t.infoPadding,
	gap: t.infoGap,
	minWidth: 0,
});

export const titleSX = (t: VariantTokens): SxProps<Theme> => ({
	color: '#2C2C2C',
	fontSize: t.titleFontSize,
	fontWeight: t.titleFontWeight,
	lineHeight: t.titleLineHeight,
	textAlign: t.textAlign,
	margin: 0,
	fontFamily: 'Open Sans, Helvetica Neue, Arial, sans-serif',
});

export const subtitleSX = (t: VariantTokens): SxProps<Theme> => ({
	color: '#2C2C2C',
	fontSize: t.subtitleFontSize,
	lineHeight: t.subtitleLineHeight,
	textAlign: t.textAlign,
	margin: 0,
	fontFamily: 'Open Sans, Helvetica Neue, Arial, sans-serif',
});

export const ctaWrapperSX = (t: VariantTokens): SxProps<Theme> => ({
	width: t.ctaAlign === 'stretch' ? '100%' : 'auto',
	alignSelf: t.ctaAlign,
	marginTop: t.ctaMarginTop,
});

export const ctaLinkSX: SxProps<Theme> = {
	padding: 0,
	minWidth: 0,
	textTransform: 'none',
	fontWeight: 500,
	justifyContent: 'flex-start',
};

export const ctaButtonSX: SxProps<Theme> = {
	textTransform: 'none',
	fontWeight: 600,
};
